import Vue from 'vue'
import { DD_ICONS } from '@/icons/ddIcons'

/**
 * Field helper texts as an info icon with a tooltip (tasks#211), as on the DoorDash merchant
 * portal's "Request a delivery" form: the `hint` of a labelled Vuetify field is not written under
 * the field any more, a 16px prism `info-line` icon follows the label text instead and the hint
 * shows in a tooltip on hover, on keyboard focus and on a tap. Error messages stay under the field.
 *
 * A global mixin covers every Vuetify input (v-text-field, v-textarea, v-select, v-autocomplete,
 * v-combobox, v-switch, v-checkbox, …), wrappers included, without template changes:
 * - `hasHint` is shadowed on the instance, so Vuetify never puts the hint into the messages line;
 * - `genLabel` is wrapped, and the icon is appended to the label it renders.
 * A field keeps its hint inline when it has no label, when its label is an in-box placeholder
 * (outlined / solo / rounded / single-line fields: search and filter boxes) and when it carries
 * the `data-inline-hint` attribute (a hint that shows a live value, e.g. a resolved date).
 *
 * The tooltip is one shared element on <body>, driven by delegated listeners: a v-tooltip per
 * field would add an instance (and its activator bookkeeping) to every hinted field of a form.
 */

export const HINT_ATTRIBUTE = 'data-dd-hint'
const TOOLTIP_ID = 'dd-hint-tooltip'
const VIEWPORT_MARGIN = 8
const GAP = 8

function inlineLayout(vm) {
    return (
        vm.outlined ||
        vm.solo ||
        vm.soloInverted ||
        vm.rounded ||
        vm.singleLine ||
        (vm.fullWidth && vm.$options.name !== 'v-textarea')
    )
}

function hintAsTooltip(vm) {
    return (
        !!vm.hint &&
        !!(vm.label || vm.$slots.label) &&
        !inlineLayout(vm) &&
        !(vm.$attrs && vm.$attrs['data-inline-hint'] !== undefined)
    )
}

function findGetter(vm, key) {
    let proto = Object.getPrototypeOf(vm)
    while (proto) {
        const descriptor = Object.getOwnPropertyDescriptor(proto, key)
        if (descriptor) return descriptor.get
        proto = Object.getPrototypeOf(proto)
    }
    return null
}

// ---- the shared tooltip ----

let tooltip = null
let anchor = null

function tooltipElement() {
    if (!tooltip) {
        tooltip = document.createElement('div')
        tooltip.id = TOOLTIP_ID
        tooltip.className = 'dd-hint-tooltip'
        tooltip.setAttribute('role', 'tooltip')
        document.body.appendChild(tooltip)
    }
    return tooltip
}

export function hideFieldHint() {
    if (!tooltip || !anchor) return
    anchor.removeAttribute('aria-describedby')
    anchor = null
    tooltip.classList.remove('dd-hint-tooltip--open')
}

export function showFieldHint(el) {
    const text = el && el.getAttribute(HINT_ATTRIBUTE)
    if (!text) return
    const box = tooltipElement()
    if (anchor && anchor !== el) anchor.removeAttribute('aria-describedby')
    anchor = el
    el.setAttribute('aria-describedby', TOOLTIP_ID)
    box.textContent = text
    // measured at the viewport's left edge, so the previous position never narrows it
    box.style.top = '0px'
    box.style.left = '0px'
    box.classList.add('dd-hint-tooltip--open')

    // above the icon, centred; below it when there is no room above; inside the viewport
    const icon = el.getBoundingClientRect()
    const width = box.offsetWidth
    const height = box.offsetHeight
    let top = icon.top - GAP - height
    if (top < VIEWPORT_MARGIN) top = icon.bottom + GAP
    let left = icon.left + icon.width / 2 - width / 2
    left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(left, window.innerWidth - VIEWPORT_MARGIN - width)
    )
    box.style.top = `${Math.round(top)}px`
    box.style.left = `${Math.round(left)}px`
}

function hintTarget(event) {
    const target = event.target
    return target && target.closest
        ? target.closest(`[${HINT_ATTRIBUTE}]`)
        : null
}

if (typeof document !== 'undefined') {
    document.addEventListener('mouseover', (event) => {
        const el = hintTarget(event)
        if (el && el !== anchor) showFieldHint(el)
    })
    document.addEventListener('mouseout', (event) => {
        const el = hintTarget(event)
        if (!el || el !== anchor) return
        if (event.relatedTarget && el.contains(event.relatedTarget)) return
        if (document.activeElement === el) return
        hideFieldHint()
    })
    document.addEventListener('focusin', (event) => {
        const el = hintTarget(event)
        if (el) showFieldHint(el)
        else hideFieldHint()
    })
    document.addEventListener('focusout', (event) => {
        if (hintTarget(event) === anchor) hideFieldHint()
    })
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' || event.key === 'Esc') hideFieldHint()
    })
    // a fixed tooltip would drift away from its icon
    window.addEventListener('scroll', hideFieldHint, true)
    window.addEventListener('resize', hideFieldHint)
}

// ---- the icon ----

/**
 * The info icon for a hint, as a vnode: also used by labels that are not a Vuetify field label
 * (FieldHintIcon.vue).
 */
export function renderFieldHint(h, hint) {
    const icon = DD_ICONS['dd-info-line']
    const paths = icon.paths.map((path) =>
        h('path', {
            attrs: {
                d: path.d,
                'fill-rule': path.evenodd ? 'evenodd' : null,
                'clip-rule': path.evenodd ? 'evenodd' : null,
            },
        })
    )
    return h(
        'span',
        {
            staticClass: 'dd-field-hint',
            attrs: {
                [HINT_ATTRIBUTE]: hint,
                'aria-label': hint,
                role: 'button',
                tabindex: 0,
            },
            on: {
                // the icon sits inside the field's <label>: a click must neither focus the input nor
                // toggle a switch; a tap (no hover on touch screens) opens or closes the tooltip
                click(event) {
                    event.preventDefault()
                    event.stopPropagation()
                    const el = event.currentTarget
                    if (anchor === el) hideFieldHint()
                    else showFieldHint(el)
                },
                mousedown(event) {
                    event.stopPropagation()
                },
                mouseup(event) {
                    event.stopPropagation()
                },
                keydown(event) {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        event.stopPropagation()
                        showFieldHint(event.currentTarget)
                    }
                },
            },
        },
        [
            h(
                'svg',
                {
                    attrs: {
                        xmlns: 'http://www.w3.org/2000/svg',
                        viewBox: icon.viewBox,
                        width: 16,
                        height: 16,
                        fill: 'currentColor',
                        'aria-hidden': 'true',
                        focusable: 'false',
                    },
                },
                paths
            ),
        ]
    )
}

Vue.mixin({
    beforeCreate() {
        const options = this.$options
        if (
            !options.computed ||
            !options.computed.hasHint ||
            !options.methods ||
            !options.methods.genLabel
        ) {
            return
        }
        const original = findGetter(this, 'hasHint')
        if (!original) return
        Object.defineProperty(this, 'hasHint', {
            configurable: true,
            get() {
                return hintAsTooltip(this) ? false : original.call(this)
            },
        })
        this.$_ddFieldHint = true
    },

    created() {
        if (!this.$_ddFieldHint || typeof this.genLabel !== 'function') return
        const genLabel = this.genLabel
        this.genLabel = (...args) => {
            const label = genLabel(...args)
            if (!label || !hintAsTooltip(this)) return label
            label.children = (label.children || []).concat(
                renderFieldHint(this.$createElement, this.hint)
            )
            return label
        }
    },

    beforeDestroy() {
        if (
            anchor &&
            this.$el &&
            this.$el.contains &&
            this.$el.contains(anchor)
        )
            hideFieldHint()
    },
})
